/**
 * Edge Utility Functions
 *
 * Helper functions for working with StreamlainEdges.
 */

import type { GfxModel } from '@blocksuite/std/gfx';

import type { EdgeMetadata } from '../core/types/edge-definition-types';
import type {
  AnchorPosition,
  EdgeEndpointMarker,
  EdgeType,
  EdgeVisualType,
  Point,
  StreamlainEdge,
} from '../types';

/**
 * Extended GfxModel with block-specific properties
 * (GfxModel doesn't include flavour, but our BlockModels do)
 */
type GfxBlockModel = GfxModel & { flavour?: string };

/**
 * Finds all edges connected to an element
 *
 * @param edges - Map of all edges
 * @param elementId - Element ID to find edges for
 * @returns Array of connected edges
 */
export function getEdgesForElement(
  edges: Map<string, StreamlainEdge>,
  elementId: string
): StreamlainEdge[] {
  return Array.from(edges.values()).filter(
    edge => edge.sourceId === elementId || edge.targetId === elementId
  );
}

/**
 * Gets the "other" element ID for an edge
 *
 * @param edge - The edge
 * @param elementId - One endpoint ID
 * @returns The other endpoint ID
 */
export function getOtherEndpoint(
  edge: StreamlainEdge,
  elementId: string
): string {
  return edge.sourceId === elementId ? edge.targetId : edge.sourceId;
}

/**
 * Extracts Database ID from an element (handles Note-wrapper case)
 *
 * @param element - GfxModel element
 * @returns Database ID or null if not a database
 */
export function getDatabaseIdFromElement(
  element: GfxBlockModel
): string | null {
  // Direct database on canvas
  if (element?.flavour === 'affine:database') {
    return element.id;
  }

  // Database in Note-wrapper
  if (element?.flavour === 'affine:note') {
    const children = (
      element as unknown as { children?: { flavour: string; id: string }[] }
    ).children;
    const firstChild = children?.[0];
    if (firstChild?.flavour === 'affine:database') {
      return firstChild.id;
    }
  }

  return null;
}

/**
 * Checks if an element is a valid edge endpoint
 *
 * @param element - GfxModel element
 * @param allowedFlavours - Allowed block flavours
 * @returns True if valid endpoint
 */
export function isValidEdgeEndpoint(
  element: GfxBlockModel,
  allowedFlavours: string[]
): boolean {
  if (!element) return false;

  // Direct check
  if (element.flavour && allowedFlavours.includes(element.flavour)) {
    return true;
  }

  // Note-wrapper check
  if (element.flavour === 'affine:note') {
    const children = (
      element as unknown as { children?: { flavour: string }[] }
    ).children;
    const firstChild = children?.[0];
    if (firstChild && allowedFlavours.includes(firstChild.flavour)) {
      return true;
    }
  }

  return false;
}

/**
 * Options for creating an edge
 */
export interface CreateEdgeOptions {
  /** Edge ID (required - use definition.id from EdgeDerivationService) */
  id: string;
  visible?: boolean;
  opacity?: number;
  highlighted?: boolean;
  /** Which side of source element to connect from */
  sourceAnchor?: AnchorPosition;
  /** Which side of target element to connect to */
  targetAnchor?: AnchorPosition;
  /** Visual type for rendering colors (derived from metadata direction) */
  visualType?: EdgeVisualType;
  /** Semantic metadata from edge derivation */
  metadata?: EdgeMetadata;
  /** Marker rendered at the source endpoint. */
  sourceMarker?: EdgeEndpointMarker;
  /** Marker rendered at the target endpoint. */
  targetMarker?: EdgeEndpointMarker;
}

/**
 * Creates a new StreamlainEdge with default values
 */
export function createEdge(
  sourceId: string,
  targetId: string,
  type: EdgeType,
  options: CreateEdgeOptions
): StreamlainEdge {
  return {
    id: options.id,
    sourceId,
    targetId,
    type,
    visible: options.visible ?? true,
    opacity: options.opacity ?? 0.3,
    highlighted: options.highlighted ?? false,
    selected: false,
    sourceAnchor: options.sourceAnchor,
    targetAnchor: options.targetAnchor,
    visualType: options.visualType,
    metadata: options.metadata,
    sourceMarker: options.sourceMarker,
    targetMarker: options.targetMarker,
  };
}

/**
 * Calculates distance between two points
 */
export function distance(p1: Point, p2: Point): number {
  return Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
}

/**
 * Checks if a point is near a path (for hit-testing)
 *
 * @param point - Point to test
 * @param pathPoints - Points along the path
 * @param threshold - Max distance to consider "near" (default: 10)
 */
export function isPointNearPath(
  point: Point,
  pathPoints: Point[],
  threshold = 10
): boolean {
  for (let i = 0; i < pathPoints.length - 1; i++) {
    const d = distanceToLineSegment(point, pathPoints[i], pathPoints[i + 1]);
    if (d < threshold) return true;
  }
  return false;
}

/**
 * Calculates distance from a point to a line segment
 */
function distanceToLineSegment(p: Point, a: Point, b: Point): number {
  const ab = { x: b.x - a.x, y: b.y - a.y };
  const ap = { x: p.x - a.x, y: p.y - a.y };
  const abLenSq = ab.x * ab.x + ab.y * ab.y;

  if (abLenSq === 0) {
    // a and b are the same point
    return distance(p, a);
  }

  let t = (ap.x * ab.x + ap.y * ab.y) / abLenSq;
  t = Math.max(0, Math.min(1, t));

  const closest = { x: a.x + t * ab.x, y: a.y + t * ab.y };
  return distance(p, closest);
}
